<template>
  <Fragment>
    <AppMenu :menu-items="menuItems" />
    <AppBar :menu-items="menuItems" />

    <VMain :data-with-banner="withBanner">
      <slot />
    </VMain>
    <UFooter />
  </Fragment>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Fragment } from 'vue-fragment'

import branchName from '@/mixins/branchName'
import theme from '@/mixins/theme'

import { initSpeechPlayer } from '@/utils/common'
import { loadBrandingTranslations } from '@/i18n/i18n'
import { newMessagesCounter } from '@/utils/messagesCounter'
import AppMenu from '@components/AppMenu.vue'
import AppBar from '@components/AppBar.vue'
import UFooter from '@components/UFooter.vue'

import * as getters from '@/store/getters/types'
import * as actions from '@/store/actions/types'

import { MenuItem } from '@/utils/menu'

export default {
  name: 'ULayoutMain',
  components: {
    Fragment,
    AppMenu,
    AppBar,
    UFooter
  },

  mixins: [branchName, theme],

  data () {
    return {
      settings: false,
      dialog: false,
      withBanner: false,
      componentsWithBanner: [
        'sessions',
        'messenger',
        'marks',
        'history',
        'meetings',
      ]
    }
  },

  computed: {
    ...mapGetters({
      account: getters.ACCOUNT,
      vendor: getters.VENDOR,
      limits: getters.VENDOR_LIMITS,
      countNewMessages: getters.COUNT_NEW_MESSAGES,
      countNewSessions: getters.COUNT_NEW_SESSIONS,
      countNewMeetings: getters.COUNT_NEW_MEETINGS,
      countNewSurveys: getters.COUNT_NEW_SURVEYS,
      countNewNews: getters.COUNT_NEW_NEWS,
      countNewSignedFiles: getters.COUNT_NEW_SIGNED_FILES
    }),

    hiddenMenuItems () {
      // U4-4790: скрыть разделы для EvGroup
      if (this.isKaEvGroup) {
        return ['surveys', 'news', 'meetings', 'online_shop', 'marks', 'messages']
      }

      // U4-4930: скрыть раздел с оценками для testkbp
      if (this.isTestkbp) {
        return ['marks']
      }

      return []
    },

    menuItems () {
      const initialMenuItems = [
        new MenuItem({
          name: 'news',
          title: '$sdo.lc_menu_news.other',
          icon: '$uchi_news',
          to: '/news',
          disabled: !this.limits.news_available,
          counter: this.countNewNews
        }),

        new MenuItem({
          name: 'library',
          title: '$sdo.lc_menu_library.one',
          icon: '$uchi_lib',
          to: '/library',
          disabled: !this.limits.infobase_available
        }),

        new MenuItem({
          name: 'programs',
          title: '$sdo.lc_menu_sessions.other',
          icon: '$uchi_programs',
          // https://youtrack.uchi.red/issue/U4-4790: у EvGroup особоый случай
          ...(this.isKaEvGroup
            ? { href: this.vendor.profile?.website ? this.vendor.profile?.website : '/' }
            : { to: '/sessions' }),
          counter: this.countNewSessions
        }),

        new MenuItem({
          name: 'surveys',
          title: '$sdo.lc_menu_surveys.other',
          icon: '$uchi_surveys',
          to: '/surveys',
          disabled: !this.limits.surveys_available,
          counter: this.countNewSurveys
        }),

        new MenuItem({
          name: 'messages',
          title: '$sdo.lc_menu_messages.other',
          icon: '$uchi_messages',
          to: '/messages',
          disabled: this.limits.messages_disabled,
          counter: this.countNewMessages
        }),

        new MenuItem({
          name: 'marks',
          title: '$sdo.lc_menu_marks.other',
          icon: '$uchi_marks',
          to: '/marks'
        }),

        new MenuItem({
          name: 'students_record_book',
          title: '$sdo.lc_menu_students_record_book.one',
          icon: '$uchi_history',
          to: '/history'
        }),

        new MenuItem({
          name: 'meetings',
          title: '$sdo.lc_menu_meetings.other',
          icon: '$uchi_meetings',
          to: '/meetings',
          disabled: !this.limits.meetings_available,
          counter: this.countNewMeetings
        })
      ]

      if (this.vendor?.branding?.show_listener_online_shop_link) {
        initialMenuItems.push(new MenuItem({
          name: 'online_shop',
          title: '$sdo.lc_menu_online_shop.one',
          icon: '$uchi_online_shop',
          href: '/shop'
        }))
      }

      if (this.limits.signed_files_available) {
        initialMenuItems.push(new MenuItem({
          name: 'signed_files',
          title: '$app.signed_files.other',
          icon: '$uchi_signed_files',
          to: '/signed-files',
          counter: this.countNewSignedFiles
        }))
      }

      return initialMenuItems.filter(item => !item.disabled && !this.hiddenMenuItems.includes(item.name))
    },
  },

  watch: {
    $route: {
      handler: function (to) {
        this.$nextTick(() => {
          this.checkBannerExistence(to.name)
        })
      }
    }
  },

  created () {
    this.register()
    this.checkBannerExistence(this.$route.name)

    initSpeechPlayer().observe(
      document.body,
      { childList: true, subtree: true }
    )
  },

  beforeDestroy () {
    this.unregister()
  },

  methods: {
    ...mapActions({
      getSessions: actions.GET_SESSIONS,
      getMeetings: actions.GET_MEETINGS,
      getSurveys: actions.GET_SURVEYS,
      getVendorLimits: actions.GET_VENDOR_LIMITS,
      getCountNewNews: actions.GET_COUNT_NEW_NEWS,
      getCountNewSignedFiles: actions.GET_COUNT_NEW_SIGNED_FILES
    }),

    async register () {
      const branding = this.vendor?.branding

      if (branding) {
        this.useBrandingTheme(branding)
      }

      loadBrandingTranslations(branding)

      await this.getVendorLimits(this.vendor.id)
      if (!this.limits?.messages_disabled) {
        newMessagesCounter.start()
      }

      if (this.$route.name !== 'sessions' && this.$route.name !== 'user-required-fields') {
        /* Незаметно загрузить список сессий, чтобы показать счетчик */
        this.getSessions(true)
      }

      if (this.$route.name !== 'meetings' && this.limits.meetings_available) {
        /* Незаметно загрузить список мероприятий, чтобы показать счетчик */
        this.getMeetings(true)
      }

      if (this.$route.name !== 'surveys' && this.limits.surveys_available) {
        /* Незаметно загрузить список опросов, чтобы показать счетчик */
        this.getSurveys(true)
      }

      if (this.$route.name !== 'singleNews' && this.limits.news_available) {
        /* Незаметно загрузить кол-во непрочитанных новостей, чтобы показать счетчик */
        this.getCountNewNews({ silent: true })
      }

      if (this.limits.signed_files_available && this.account?.profile?.signed_files_policy_accepted) {
        /* Незаметно загрузить кол-во файлов на подпись, чтобы показать счетчик */
        this.getCountNewSignedFiles({ silent: true })
      }
    },

    unregister () {
      if (!this.limits?.messages_disabled) {
        newMessagesCounter.stop()
      }
    },

    checkBannerExistence (name) {
      if (this.componentsWithBanner.includes(name)) {
        this.withBanner = true
      } else {
        this.withBanner = false
      }
    },
  }
}
</script>
